"use client"

import * as React from "react"
import { Check, ChevronsUpDown, Search, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

export interface MultiSelectOption {
  value: string
  label: string
  icon?: React.ReactNode
  description?: string
}

interface MultiSelectProps {
  options: MultiSelectOption[]
  value?: string[]
  onValueChange?: (value: string[]) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  className?: string
  maxHeight?: string
}

export function MultiSelect({
  options,
  value = [],
  onValueChange,
  placeholder = "Select options...",
  searchPlaceholder = "Search...",
  emptyText = "No options found.",
  disabled = false,
  className,
  maxHeight = "300px",
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [search, setSearch] = React.useState("")

  const selectedOptions = options.filter((option) => value.includes(option.value))

  const filteredOptions = React.useMemo(() => {
    if (!search) return options
    
    return options.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase()) ||
      option.value.toLowerCase().includes(search.toLowerCase()) ||
      option.description?.toLowerCase().includes(search.toLowerCase())
    )
  }, [options, search])

  const handleToggleOption = (optionValue: string) => {
    const newValue = value.includes(optionValue)
      ? value.filter(v => v !== optionValue)
      : [...value, optionValue]
    onValueChange?.(newValue)
    // Clear search after selection for better UX
    setSearch("")
  }

  const handleRemoveOption = (optionValue: string, event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    const newValue = value.filter(v => v !== optionValue)
    onValueChange?.(newValue)
  }

  const handleClearAll = (event: React.MouseEvent) => {
    event.preventDefault()
    event.stopPropagation()
    onValueChange?.([])
  }

  // Clear search when popover closes
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen)
    if (!newOpen) {
      setSearch("")
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between min-h-[40px] h-auto pr-3", className)}
          disabled={disabled}
        >
          <div className="flex items-center w-full min-w-0">
            <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0 mr-2">
              {selectedOptions.length === 0 ? (
                <span className="text-muted-foreground truncate">{placeholder}</span>
              ) : (
                selectedOptions.map((option) => (
                  <Badge
                    key={option.value}
                    variant="secondary"
                    className="text-xs shrink-0"
                  >
                    <span className="truncate max-w-[120px]">{option.label}</span>
                    <div
                      className="ml-1 h-3 w-3 p-0 hover:bg-transparent cursor-pointer rounded-sm hover:bg-gray-200 flex items-center justify-center"
                      onClick={(e) => handleRemoveOption(option.value, e)}
                    >
                      <X className="h-2 w-2" />
                    </div>
                  </Badge>
                ))
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 w-full z-[100]"
        align="start"
        side="bottom"
        sideOffset={4}
        style={{
          width: 'var(--radix-popover-trigger-width)',
          maxWidth: 'var(--radix-popover-trigger-width)'
        }}
      >
        <div className="border-b px-3 py-2">
          <div className="flex items-center">
            <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
            <Input
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="border-0 p-0 focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:text-muted-foreground"
            />
          </div>
        </div>
        <div
          className="overflow-y-auto overscroll-contain"
          style={{
            maxHeight: maxHeight,
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgb(203 213 225) transparent',
          }}
          onWheel={(e) => e.stopPropagation()}
        >
          <div className="p-1">
            {filteredOptions.length === 0 ? (
              <div className="py-6 text-center text-sm text-muted-foreground">
                {emptyText}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = value.includes(option.value)
                return (
                  <div
                    key={option.value}
                    className={cn(
                      "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                      isSelected && "bg-accent/50"
                    )}
                    onClick={() => handleToggleOption(option.value)}
                  >
                    {isSelected && (
                      <Check className="mr-2 h-4 w-4" />
                    )}
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="truncate">{option.label}</span>
                      {option.description && (
                        <span className="text-xs text-muted-foreground truncate">
                          {option.description}
                        </span>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
        {selectedOptions.length > 0 && (
          <div className="border-t px-3 py-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start"
              onClick={handleClearAll}
            >
              Clear All ({selectedOptions.length})
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}